// src/pages/StudentPortal.js
// ──────────────────────────────────────────────────────────────────────────
// Student portal — dashboard shell with sections: Overview, My Classes,
// Announcements, Assignments, Documents, Results. Browse-only.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PortalShell from '../components/portal/PortalShell';
import { useAuth } from '../context/AuthContext';
import {
  fetchMyEnrolments, fetchAnnouncements, fetchAssignments,
  fetchDocuments, fetchResults, fetchNotices, fetchMySubmissions, submitWork, uploadFile,
} from '../lib/cms';
import theme from '../theme/theme';

const Note = styled.div`
  max-width: 520px; margin: 5rem auto; text-align: center; padding: 0 1.5rem;
  h2 { font-weight: 400; font-size: 1.8rem; } p { color: ${theme.color.inkSoft}; margin: 0.6rem 0 1.4rem; }
  a { background: ${theme.color.accent}; color: #fff; font-weight: 700; padding: 12px 26px; border-radius: ${theme.layout.radius}; }
`;
const Item = styled.div`
  border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 1.2rem 1.4rem; margin-bottom: 0.8rem; background: #fff;
  h3 { font-weight: 600; font-size: 1.05rem; } p { margin: 0.3rem 0 0; color: ${theme.color.inkSoft}; font-size: 0.93rem; white-space: pre-wrap; }
  .tag { font-size: 0.7rem; color: ${theme.color.accent}; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; }
  .due { font-size: 0.8rem; color: #b3261e; font-weight: 600; } a { color: ${theme.color.accent}; font-weight: 700; font-size: 0.9rem; }
`;
const Empty = styled.p` color: ${theme.color.inkSoft}; `;
const Cards = styled.div` display: grid; grid-template-columns: repeat(auto-fill, minmax(220px,1fr)); gap: 1rem; `;
const ClassCard = styled.div`
  border: 1px solid ${theme.color.line}; border-left: 4px solid ${theme.color.accent}; border-radius: ${theme.layout.radius}; padding: 1.2rem 1.4rem; background: #fff;
  .code { font-size: 0.72rem; color: ${theme.color.accent}; font-weight: 700; } h3 { font-weight: 600; font-size: 1.1rem; margin-top: 0.2rem; }
  p { color: ${theme.color.inkSoft}; font-size: 0.88rem; margin-top: 0.3rem; }
`;
const H = styled.h2` font-weight: 400; font-size: 1.5rem; margin-bottom: 1rem; `;

const SECTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'classes', label: 'My classes' },
  { key: 'notices', label: 'Notices' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'documents', label: 'Documents' },
  { key: 'results', label: 'My results' },
];

// Admin-posted notices carry an audience: "all", "student" or "staff".
// Only show the ones meant for students (treat a missing value as "all").
const forStudents = (row) => !row.audience || row.audience === 'all' || row.audience === 'student';

function AssignmentItem({ a, sub, onDone, fmt }) {
  const [note, setNote] = useState(sub?.note || '');
  const [fileUrl, setFileUrl] = useState(sub?.file_url || '');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const graded = Boolean(sub?.graded_at);

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const url = await uploadFile(f); setFileUrl(url); } catch (er) { alert(er.message); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { await submitWork({ assignmentId: a.id, note, fileUrl }); setOpen(false); onDone(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <Item>
      <span className="tag">{a.classes?.name || 'Class'}</span>
      <h3>{a.title}</h3>
      {a.instructions && <p>{a.instructions}</p>}
      {a.due_date && <p className="due">Due {fmt(a.due_date)}</p>}
      {a.file_url && <p><a href={a.file_url} target="_blank" rel="noreferrer">Open attachment ↗</a></p>}

      <div style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: `1px solid ${theme.color.line}` }}>
        {graded ? (
          <div>
            <span className="tag" style={{ color: '#1a7f4b' }}>Graded: {sub.grade}</span>
            {sub.feedback && <p style={{ marginTop: '0.3rem' }}>{sub.feedback}</p>}
          </div>
        ) : sub ? (
          <div>
            <span style={{ fontSize: '0.85rem', color: theme.color.inkSoft }}>✓ Submitted{sub.file_url ? ' (with file)' : ''}. You can update it until it's graded.</span>
            <button onClick={() => setOpen(!open)} style={{ marginLeft: 8, background: 'none', border: 'none', color: theme.color.accent, fontWeight: 700, cursor: 'pointer' }}>{open ? 'Cancel' : 'Update'}</button>
          </div>
        ) : (
          <button onClick={() => setOpen(!open)} style={{ background: theme.color.accent, color: '#fff', border: 'none', fontWeight: 700, padding: '8px 16px', borderRadius: theme.layout.radius, cursor: 'pointer' }}>{open ? 'Cancel' : 'Submit work'}</button>
        )}

        {open && !graded && (
          <div style={{ marginTop: '0.8rem' }}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notes for your teacher (optional)"
              style={{ width: '100%', minHeight: 70, padding: '10px 12px', border: `1px solid ${theme.color.line}`, borderRadius: theme.layout.radius, fontFamily: theme.font.body }} />
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.5rem' }}>
              {fileUrl && <a href={fileUrl} target="_blank" rel="noreferrer" style={{ color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>Attached file ↗</a>}
              <label style={{ cursor: 'pointer', color: theme.color.accent, fontWeight: 700, fontSize: '0.85rem' }}>{busy ? 'Uploading…' : 'Attach file'}<input type="file" onChange={pick} style={{ display: 'none' }} /></label>
              <button onClick={send} disabled={busy} style={{ marginLeft: 'auto', background: theme.color.accent, color: '#fff', border: 'none', fontWeight: 700, padding: '8px 16px', borderRadius: theme.layout.radius, cursor: 'pointer' }}>{busy ? 'Saving…' : 'Send'}</button>
            </div>
          </div>
        )}
      </div>
    </Item>
  );
}

export default function StudentPortal() {
  const { user, loading, ready } = useAuth();
  const [tab, setTab] = useState('overview');
  const [classes, setClasses] = useState([]);
  const [ann, setAnn] = useState([]);
  const [asg, setAsg] = useState([]);
  const [docs, setDocs] = useState([]);
  const [results, setResults] = useState([]);
  const [notices, setNotices] = useState([]);
  const [subs, setSubs] = useState([]);

  const reloadSubs = () => fetchMySubmissions().then(setSubs);

  useEffect(() => {
    if (!ready || !user) return;
    fetchMyEnrolments().then(setClasses);
    fetchAnnouncements().then(setAnn);
    fetchAssignments().then(setAsg);
    fetchDocuments().then(setDocs);
    fetchResults().then(setResults);
    fetchNotices().then((rows) => setNotices((rows || []).filter(forStudents)));
    reloadSubs();
  }, [ready, user]);

  if (!ready) return (<><Navbar /><Note><h2>Portal not connected</h2><p>Add your Supabase keys to enable the portal.</p><Link to="/">Back home</Link></Note><Footer /></>);
  if (loading) return (<><Navbar /><Note><h2>Loading…</h2></Note><Footer /></>);
  if (!user) return (<><Navbar /><Note><h2>Please sign in</h2><p>The student portal is for enrolled students.</p><Link to="/login">Go to login</Link></Note><Footer /></>);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '');

  return (
    <>
      <PortalShell kind="Student" sections={SECTIONS} active={tab} onSelect={setTab}>
        {tab === 'overview' && (
          <>
            <H>Quick overview</H>
            <Cards>
              <ClassCard><div className="code">CLASSES</div><h3>{classes.length}</h3><p>You’re enrolled in {classes.length} class{classes.length === 1 ? '' : 'es'}.</p></ClassCard>
              <ClassCard><div className="code">ASSIGNMENTS</div><h3>{asg.length}</h3><p>Across your classes.</p></ClassCard>
              <ClassCard><div className="code">ANNOUNCEMENTS</div><h3>{ann.length}</h3><p>Recent updates.</p></ClassCard>
              <ClassCard><div className="code">NOTICES</div><h3>{notices.length}</h3><p>From the institute.</p></ClassCard>
              <ClassCard><div className="code">RESULTS</div><h3>{results.length}</h3><p>Published to you.</p></ClassCard>
            </Cards>
          </>
        )}

        {tab === 'classes' && (
          <>
            <H>My classes</H>
            {classes.length === 0 ? <Empty>You’re not enrolled in any classes yet. Your department will enrol you.</Empty> : (
              <Cards>
                {classes.map((c) => (
                  <ClassCard key={c.id}><div className="code">{c.code || c.department}</div><h3>{c.name}</h3>{c.description && <p>{c.description}</p>}</ClassCard>
                ))}
              </Cards>
            )}
          </>
        )}

        {tab === 'notices' && (
          <>
            <H>Notices</H>
            {notices.length === 0 ? <Empty>No notices from the institute yet.</Empty> : notices.map((n) => (
              <Item key={n.id}>
                <span className="tag">Institute · {fmt(n.created_at)}</span>
                <h3>{n.title}</h3>{n.body && <p>{n.body}</p>}
              </Item>
            ))}
          </>
        )}

        {tab === 'announcements' && (
          <>
            <H>Announcements</H>
            {ann.length === 0 ? <Empty>No announcements yet.</Empty> : ann.map((a) => (
              <Item key={a.id}>
                <span className="tag">{a.classes?.name || a.audience} · {fmt(a.created_at)}</span>
                <h3>{a.title}</h3>{a.body && <p>{a.body}</p>}
              </Item>
            ))}
          </>
        )}

        {tab === 'assignments' && (
          <>
            <H>Assignments</H>
            {asg.length === 0 ? <Empty>No assignments yet.</Empty> : asg.map((a) => (
              <AssignmentItem key={a.id} a={a} sub={subs.find((s) => s.assignment_id === a.id)} onDone={reloadSubs} fmt={fmt} />
            ))}
          </>
        )}

        {tab === 'documents' && (
          <>
            <H>Documents</H>
            {docs.length === 0 ? <Empty>No documents yet.</Empty> : docs.map((d) => (
              <Item key={d.id}><h3>{d.title}</h3>{d.url && <a href={d.url} target="_blank" rel="noreferrer">Open ↗</a>}</Item>
            ))}
          </>
        )}

        {tab === 'results' && (
          <>
            <H>My results</H>
            {results.length === 0 ? <Empty>No results published yet.</Empty> : results.map((r) => (
              <Item key={r.id}><span className="tag">{fmt(r.created_at)}</span><h3>{r.title}</h3>{r.detail && <p>{r.detail}</p>}</Item>
            ))}
          </>
        )}
      </PortalShell>
      <Footer />
    </>
  );
}
